/**
 * Page-local URL params for InquiriesPage's two list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: the status and priority filters (both feed the
 * `filters` object passed into the `inquiries` query key). Sixth adoption of the pattern #78
 * established (after #97, #98, #99, #100).
 *
 * The detail panel (`selectedInquiry`, `detailPanelOpen`) and the inline quick-reply composer
 * (`quickReplyOpen`, `quickReplyInquiry`, `quickReplyText`) are deliberately OUT of scope: both
 * are reset on close/success, tied to one inquiry or one action, not view configuration -- same
 * call made for dialog-local state on other pages in this sweep.
 *
 * Conventions (mirroring the other hooks in this sweep):
 * - Each param is present only when set; an unrecognised value falls back to undefined rather
 *   than leaking a bad enum value into state.
 * - Mutations replace history (never push).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { InquiryStatus, InquiryPriority } from '../types';

const VALID_STATUSES: readonly InquiryStatus[] = [
  'new', 'in_progress', 'waiting_for_client', 'resolved', 'closed',
];
const VALID_PRIORITIES: readonly InquiryPriority[] = ['urgent', 'high', 'normal', 'low'];

export interface InquiriesScopeParams {
  status: InquiryStatus | undefined;
  priority: InquiryPriority | undefined;
  setStatus: (status: InquiryStatus | undefined) => void;
  setPriority: (priority: InquiryPriority | undefined) => void;
}

export function useInquiriesScopeParams(): InquiriesScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const status = useMemo<InquiryStatus | undefined>(() => {
    const raw = searchParams.get('status');
    return raw && (VALID_STATUSES as string[]).includes(raw) ? (raw as InquiryStatus) : undefined;
  }, [searchParams]);

  const priority = useMemo<InquiryPriority | undefined>(() => {
    const raw = searchParams.get('priority');
    return raw && (VALID_PRIORITIES as string[]).includes(raw) ? (raw as InquiryPriority) : undefined;
  }, [searchParams]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatus = useCallback(
    (next: InquiryStatus | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('status');
        else params.set('status', next);
      });
    },
    [update]
  );

  const setPriority = useCallback(
    (next: InquiryPriority | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('priority');
        else params.set('priority', next);
      });
    },
    [update]
  );

  return { status, priority, setStatus, setPriority };
}
